import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { QuoteTemplateService } from '../../../../services/forwarding/quote-template.service';
import { ContainerLoadTypesService, ContainerLoadType } from '../../../../services/forwarding/container-load-types.service';
import { IncotermService } from '../../../../services/forwarding/incoterm.service';
import { OperationalChargesService } from '../../../../services/forwarding/operational-charges.service';
import { QuoteTemplate, CreateQuoteTemplateDto, UpdateQuoteTemplateDto } from '../../../../models/forwarding/quote-template.model';
import { Incoterm } from '../../../../models/forwarding/incoterm.model';
import { OperationalCharge } from '../../../../models/forwarding/operational-charge.model';

@Component({
  selector: 'app-quote-templates-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './quote-templates-modal.html',
  styleUrl: './quote-templates-modal.css'
})
export class QuoteTemplatesModalComponent implements OnInit, OnDestroy {
  isOpen = false;
  showCreateModal = false;

  // Data
  templates: QuoteTemplate[] = [];
  filteredTemplates: QuoteTemplate[] = [];
  containerLoadTypes: ContainerLoadType[] = [];
  incoterms: Incoterm[] = [];
  operationalCharges: OperationalCharge[] = [];
  filteredOperationalCharges: OperationalCharge[] = [];

  // Filters
  searchTerm = '';
  filterMode = '';
  filterLoadType = '';
  filterStatus = '';

  // Create form
  newTemplate: CreateQuoteTemplateDto = {
    name: '',
    shipmentMode: 'SEA',
    containerLoadType: '',
    incotermScope: 'ANY',
    status: 'draft',
    isDefault: false,
    htmlContent: '',
    isActive: true
  };

  // Dropdown options
  shipmentModes = ['SEA', 'AIR'];
  statuses = ['active', 'draft', 'archived'];

  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private quoteTemplateService: QuoteTemplateService,
    private containerLoadTypesService: ContainerLoadTypesService,
    private incotermService: IncotermService,
    private operationalChargesService: OperationalChargesService
  ) {}

  ngOnInit(): void {
    this.loadTemplates();
    this.loadContainerLoadTypes();
    this.loadIncoterms();
    this.loadOperationalCharges();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadTemplates(): void {
    this.subscriptions.push(
      this.quoteTemplateService.getTemplates().subscribe({
        next: (templates) => {
          this.templates = templates;
          this.applyFilters();
        },
        error: (error) => console.error('Error loading templates:', error)
      })
    );
  }

  private loadContainerLoadTypes(): void {
    this.subscriptions.push(
      this.containerLoadTypesService.getLoadTypes().subscribe({
        next: (types) => {
          this.containerLoadTypes = types.filter(t => t.isActive);
        },
        error: (error) => console.error('Error loading container load types:', error)
      })
    );
  }

  private loadIncoterms(): void {
    this.subscriptions.push(
      this.incotermService.getIncoterms().subscribe({
        next: (incoterms) => {
          this.incoterms = incoterms.filter(i => i.isActive);
        },
        error: (error) => console.error('Error loading incoterms:', error)
      })
    );
  }

  private loadOperationalCharges(): void {
    this.subscriptions.push(
      this.operationalChargesService.getCharges().subscribe({
        next: (charges) => {
          this.operationalCharges = charges.filter(c => c.isActive);
          this.filteredOperationalCharges = this.operationalCharges;
        },
        error: (error) => console.error('Error loading operational charges:', error)
      })
    );
  }

  applyFilters(): void {
    let result = [...this.templates];

    // Search filter
    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(t =>
        t.name.toLowerCase().includes(term)
      );
    }

    // Mode filter
    if (this.filterMode) {
      result = result.filter(t => t.shipmentMode === this.filterMode);
    }

    // Load type filter
    if (this.filterLoadType) {
      result = result.filter(t => t.containerLoadType === this.filterLoadType);
    }

    // Status filter
    if (this.filterStatus) {
      result = result.filter(t => t.status === this.filterStatus);
    }

    this.filteredTemplates = result;
  }

  open(): void {
    console.log('[QuoteTemplatesModal] Opening modal');
    this.isOpen = true;
    this.quoteTemplateService.refresh();
    this.containerLoadTypesService.refresh();
    this.incotermService.refresh();
  }

  close(): void {
    console.log('[QuoteTemplatesModal] Closing modal');
    this.isOpen = false;
    this.showCreateModal = false;
  }

  openCreateModal(): void {
    this.newTemplate = {
      name: '',
      shipmentMode: 'SEA',
      containerLoadType: '',
      incotermScope: 'ANY',
      status: 'draft',
      isDefault: false,
      htmlContent: '',
      isActive: true
    };
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
  }

  createTemplate(): void {
    if (!this.newTemplate.name || !this.newTemplate.shipmentMode) {
      alert('Please fill in required fields');
      return;
    }

    this.quoteTemplateService.create(this.newTemplate).subscribe({
      next: () => {
        console.log('Template created successfully');
        this.closeCreateModal();
        this.quoteTemplateService.refresh();
      },
      error: (error) => {
        console.error('Error creating template:', error);
        alert('Error creating template: ' + (error.error?.message || error.message));
      }
    });
  }

  editTemplate(template: QuoteTemplate): void {
    console.log('[QuoteTemplatesModal] Navigating to edit template page:', template);
    // Navigate to the edit template page
    this.router.navigate(['/dashboard/forwarding/admin/quote-templates/edit', template.id]);
  }

  copyTemplate(template: QuoteTemplate): void {
    const copy: CreateQuoteTemplateDto = {
      name: `${template.name} (copy)`,
      shipmentMode: template.shipmentMode,
      containerLoadType: template.containerLoadType || undefined,
      incotermScope: template.incotermScope || undefined,
      status: 'draft',
      isDefault: false,
      htmlContent: template.htmlContent || undefined,
      isActive: true
    };

    this.quoteTemplateService.create(copy).subscribe({
      next: () => {
        console.log('Template copied successfully');
        this.quoteTemplateService.refresh();
      },
      error: (error) => {
        console.error('Error copying template:', error);
        alert('Error copying template: ' + (error.error?.message || error.message));
      }
    });
  }

  setAsDefault(template: QuoteTemplate): void {
    if (!confirm(`Set "${template.name}" as the default template?`)) {
      return;
    }

    this.quoteTemplateService.setDefault(template.id).subscribe({
      next: () => {
        console.log('Default template set successfully');
        this.quoteTemplateService.refresh();
      },
      error: (error) => {
        console.error('Error setting default template:', error);
        alert('Error setting default: ' + (error.error?.message || error.message));
      }
    });
  }

  deleteTemplate(template: QuoteTemplate): void {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    this.quoteTemplateService.delete(template.id).subscribe({
      next: () => {
        console.log('Template deleted successfully');
        this.quoteTemplateService.refresh();
      },
      error: (error) => {
        console.error('Error deleting template:', error);
        alert('Error deleting template: ' + (error.error?.message || error.message));
      }
    });
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active': return 'bg-primary';
      case 'draft': return 'bg-secondary';
      case 'archived': return 'bg-warning';
      default: return 'bg-secondary';
    }
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  }
}
